// frontend/client/src/services/api.service.ts
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { authService } from './auth.service';
import { getServerUrl, getCurrentApiUrl } from '../config/server.config';

// Interfaz para respuestas de error de la API
interface ApiErrorResponse {
  message?: string;
  error?: string;
  errors?: string[];
  [key: string]: any;
}

// Obtener URL de la API desde la configuración del servidor (localStorage)
// Si no hay configuración, usar getCurrentApiUrl que maneja el entorno automáticamente
const getAPIUrl = () => {
  const configUrl = getServerUrl(true); // true para incluir /api
  return configUrl || getCurrentApiUrl();
};

// Crear instancia de Axios con configuración base
const apiClient = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': '*/*',
  },
  withCredentials: false, // Configurar según necesidad
});

// Interceptor para establecer baseURL dinámicamente y agregar token
apiClient.interceptors.request.use(
  (config) => {
    // Establecer baseURL dinámicamente en cada petición
    config.baseURL = getAPIUrl();
    console.log('🌐 API Request to:', config.baseURL + config.url);

    const token = authService.getStoredToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    // Manejar errores de autenticación
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Solo mostrar mensaje si no es una petición silenciosa
      if (!(error.config as any)?.silentAuth) {
        toast.error('Sesión expirada. Por favor inicia sesión nuevamente.');
      }
      authService.logout();
      window.location.href = '/';
    } 
    // Mostrar errores genéricos
    else if (error.response) {
      const errorData = error.response.data || {};
      const errorMessage = 
        errorData.message || 
        errorData.error || 
        (errorData.errors && errorData.errors.length > 0 ? errorData.errors[0] : null) ||
        'Ha ocurrido un error. Por favor, inténtalo nuevamente.';
      
      // Solo mostrar toast si no es una petición silenciosa
      if (!(error.config as any)?.silentError) {
        toast.error(errorMessage);
      }
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
      if (!(error.config as any)?.silentError) {
        toast.error('No se pudo conectar al servidor. Verifica tu conexión a internet.');
      }
      console.error('Error de conexión:', error.request);
    } else {
      // Error al configurar la petición
      if (!(error.config as any)?.silentError) {
        toast.error('Error al procesar la solicitud.');
      }
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Servicio API centralizado
export const apiService = {
  // Método GET
  get: async <T = any>(
    endpoint: string, 
    config?: AxiosRequestConfig
  ): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await apiClient.get<T>(endpoint, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Método POST
  post: async <T = any>(
    endpoint: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await apiClient.post<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Método PUT
  put: async <T = any>(
    endpoint: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await apiClient.put<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Método DELETE
  delete: async <T = any>(
    endpoint: string, 
    config?: AxiosRequestConfig
  ): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await apiClient.delete<T>(endpoint, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Método PATCH
  patch: async <T = any>(
    endpoint: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await apiClient.patch<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Método para subir archivos
  upload: async <T = any>(
    endpoint: string, 
    formData: FormData, 
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const uploadConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: config?.onUploadProgress,
    };

    try {
      const response: AxiosResponse<T> = await apiClient.post<T>(endpoint, formData, uploadConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtener la URL base de la API (dinámica)
  getBaseUrl: () => getAPIUrl(),
};

export default apiService;